import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

// icons
import { PiSquaresFourThin } from "react-icons/pi";
import { AiOutlineStock } from "react-icons/ai";
import { IoIosSettings } from "react-icons/io";
import { IoLogOut } from "react-icons/io5";
import { FaWallet, FaBalanceScale, FaChartBar, FaBook, FaServer, FaStore, FaTicketAlt } from "react-icons/fa";
import { IoChevronBack, IoChevronForward, IoMenu, IoClose } from "react-icons/io5";

interface Props { }

function SideBar(props: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Parse user role and details from token to scope visibility of the Company tab
  const token = localStorage.getItem("token");
  const storedRole = localStorage.getItem("role");
  let decoded: any = null;
  if (token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      decoded = JSON.parse(jsonPayload);
    } catch (e) {}
  }

  const isSuperAdmin = storedRole === 'super-admin';
  const rawMenuItems = [
    { to: "/pos", icon: <FaStore />, label: "POS Terminal", key: "pos" },
    { to: "/customer", icon: <FaWallet />, label: "Customer", key: "customer" },
    { to: "/vendors", icon: <FaStore />, label: "Vendors", key: "vendors" },
    ...(isSuperAdmin ? [{ to: "/company", icon: <AiOutlineStock />, label: "Company", key: "company" }] : []),
    { to: "/InvoiceCreator", icon: <FaBalanceScale />, label: "Invoice Creator", key: "invoice-creator" },
    { to: "/Invoices", icon: <FaBalanceScale />, label: "Invoices", key: "invoices" },
    { to: "/purchase-invoices", icon: <FaWallet />, label: "Purchase Bills", key: "purchase-bills" },
    { to: "/purchase-invoice-creator", icon: <AiOutlineStock />, label: "Record Purchase", key: "record-purchase" },
    { to: "/recurring-invoices", icon: <IoIosSettings />, label: "Recurring", key: "recurring" },
    { to: "/statement-upload", icon: <FaWallet />, label: "Upload Statement", key: "upload-statement" },
    { to: "/reports", icon: <FaChartBar />, label: "Reports", key: "reports" },
    { to: "/party-ledger", icon: <FaBook />, label: "Party Ledger", key: "party-ledger" },
    { to: "/services", icon: <FaServer />, label: "IT Services", key: "it-services" },
    { to: "/quotations", icon: <FaBook />, label: "Quotations", key: "quotations" },
    { to: "/contracts", icon: <FaBook />, label: "Contracts", key: "contracts" },
    { to: "/credit-notes", icon: <FaTicketAlt />, label: "Credit Notes", key: "credit-notes" },
    { to: "/debit-notes", icon: <FaTicketAlt />, label: "Debit Notes", key: "debit-notes" },
    { to: "/inventory", icon: <AiOutlineStock />, label: "Inventory", key: "inventory" },
  ];

  // Filter based on role and permissions
  const permissionsStr = localStorage.getItem("permissions");
  let userPermissions: string[] | null = null;
  if (permissionsStr) {
    try {
      userPermissions = JSON.parse(permissionsStr);
    } catch (e) {}
  }

  const allowedMenuItems = rawMenuItems.filter(item => {
    if (isSuperAdmin || storedRole === 'company') return true;
    if (!userPermissions) return true; // Default to allow all if not set yet
    return userPermissions.includes(item.key);
  });

  // Sort menu items alphabetically
  allowedMenuItems.sort((a, b) => a.label.localeCompare(b.label));

  // Prepend Dashboard to be first
  const menuList = [
    { to: "/", icon: <PiSquaresFourThin />, label: "Dashboard" },
    ...allowedMenuItems
  ];

  const generalList = [
    { to: "/setting", icon: <IoIosSettings />, label: "Settings" },
    { to: "/billing", icon: <FaWallet />, label: "Billing & Plans" },
    { to: "/logout", icon: <IoLogOut />, label: "Logout" },
  ];

  const ACTIVE_BG = "bg-[#ddd6fe]";
  const HOVER_BG = "hover:bg-[#eef2ff]";
  const ACTIVE_ICON_BG = "bg-[#6D28D9]";
  const ACTIVE_ICON_COLOR = "text-white";
  const ICON_DEFAULT = "text-[#6D28D9]";
  const ICON_HOVER = "group-hover:text-[#4c1d95]";
  const ACTIVE_LABEL = "text-[#4c1d95]";
  const MUTED_TEXT = "text-[#475569]";
  const SECTION_HEADER = "text-[#5b4e7e]";
  const SECTION_HEADER_MUTED = "text-[#b8b6d6]";

  const sidebarWidth = collapsed ? "w-[64px]" : "w-[220px]";

  const renderNavItem = (item: { to: string; icon: React.ReactNode; label: string }, isGeneral = false) => (
    <NavLink
      key={item.to}
      to={item.to}
      onClick={() => setMobileOpen(false)}
      className={({ isActive }) =>
        `group flex items-center gap-3 py-2.5 rounded-lg transition-all
        ${isActive ? `${isGeneral ? 'bg-[#f3e8ff]' : ACTIVE_BG} shadow` : `${HOVER_BG} hover:shadow`}
        ${collapsed ? "justify-center px-0 rounded-full" : "px-2"}`
      }
    >
      {({ isActive }) => (
        <>
          <div
            className={`min-w-[34px] min-h-[34px] flex items-center justify-center transition-all duration-200 rounded-md 
              ${isActive
                ? `${isGeneral ? 'bg-[#a78bfa]' : ACTIVE_ICON_BG} shadow scale-105`
                : "bg-white border border-[#d6bcfa] scale-100"
              }`}
            style={{
              boxShadow: isActive ? (isGeneral ? "0 2px 8px 0 #ddd6fe" : "0 4px 14px 0 #c4b5fd") : undefined,
              border: isActive ? "none" : undefined,
            }}
          >
            <span className={`${isActive ? ACTIVE_ICON_COLOR : ICON_DEFAULT + ' ' + ICON_HOVER} text-lg`}>
              {item.icon}
            </span>
          </div>
          {(!collapsed || mobileOpen) && (
            <span
              className={`whitespace-nowrap ml-1 font-medium transition-all duration-200 opacity-100
                ${isActive
                  ? (isGeneral ? 'text-[#6D28D9] font-semibold' : `${ACTIVE_LABEL} font-bold`)
                  : `${isGeneral ? SECTION_HEADER_MUTED : MUTED_TEXT} group-hover:text-[#6D28D9]`}
                text-[14px]`}
              style={{ letterSpacing: ".01rem" }}
            >
              {item.label}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  const sidebarContent = (
    <div className="flex-1 flex flex-col min-h-0 bg-[#f8fafc]">
      {/* Scrollable Menu Items */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 flex flex-col gap-y-4 pb-4">
        {/* Collapse button - desktop only */}
        <div className={`hidden md:flex items-center ${collapsed ? 'justify-center' : 'justify-end'} mb-1 mt-3 ${collapsed ? '' : 'mr-2'}`}>
          <button
            className="rounded-full border-2 border-[#d1c4e9] bg-white hover:bg-[#ede9fe] shadow p-1 transition-all duration-200"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            type="button"
          >
            {collapsed
              ? <IoChevronForward className="text-lg text-[#6D28D9]" />
              : <IoChevronBack className="text-lg text-[#6D28D9]" />
            }
          </button>
        </div>
        <div>
          <p className={`text-xs font-semibold tracking-widest ${SECTION_HEADER} ml-2 mb-2 uppercase transition-opacity duration-200 ${collapsed && !mobileOpen ? 'opacity-0 h-0 overflow-hidden' : 'opacity-95'}`}>Menu</p>
          <nav className="flex flex-col gap-0.5 px-1">
            {menuList.map((item) => renderNavItem(item))}
          </nav>
        </div>
      </div>

      {/* General section (pinned to bottom) */}
      <div className="pt-2 pb-4 border-t border-[#e6e6f0] mt-auto px-1 bg-[#f8fafc]">
        <p className={`text-xs font-semibold tracking-widest ${SECTION_HEADER_MUTED} ml-2 uppercase mb-1 transition-all duration-200 ${collapsed && !mobileOpen ? 'opacity-0 h-0 overflow-hidden' : 'opacity-85'}`}>General</p>
        <nav className="flex flex-col gap-0.5">
          {generalList.map((item) => renderNavItem(item, true))}
        </nav>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        className="md:hidden fixed top-3 left-3 z-[60] rounded-lg bg-white border-2 border-[#d1c4e9] shadow-lg p-2 hover:bg-[#ede9fe] transition-all"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle sidebar"
        type="button"
      >
        {mobileOpen
          ? <IoClose className="text-xl text-[#6D28D9]" />
          : <IoMenu className="text-xl text-[#6D28D9]" />
        }
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-[45] bg-black/40 transition-opacity"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={`md:hidden fixed top-0 left-0 z-[50] h-full w-[260px] bg-[#f8fafc] shadow-2xl border-r border-[#e0e7ef]
          transform transition-transform duration-300 ease-in-out flex flex-col pt-20
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ boxShadow: "0 6px 32px 0 #ede9fe, 4px 0 24px 0 #f5f3ff inset" }}
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar — static, not sticky/fixed */}
      <aside
        className={`hidden md:flex flex-col flex-shrink-0 h-full bg-[#f8fafc] border-r border-[#e0e7ef] transition-all duration-300 ${sidebarWidth}`}
        style={{ boxShadow: "0 6px 32px 0 #ede9fe, 4px 0 24px 0 #f5f3ff inset" }}
      >
        {sidebarContent}
      </aside>
    </>
  );
}

export default SideBar
